import { supabase } from "@/services/supabase/client";
import { Database } from "@/services/supabase/types";
import { loadKeyFromDB } from "@/features/encryption/utils/storage";
import { processFolderName, prepareEncryptedFolderName } from "../utils/folder-encryption";
import { FolderColor } from "@/features/folders/types";

type DbFolders = Database["public"]["Tables"]["folders"];
export interface FolderRow extends Omit<DbFolders["Row"], "color"> {
  color: FolderColor | null;
}
export interface FolderInsert extends Omit<DbFolders["Insert"], "color"> {
  color: FolderColor | null;
}
export interface FolderUpdate extends Partial<Omit<DbFolders["Update"], "color">> {
  color?: FolderColor | null;
}

export interface FetchedFolder
  extends Pick<
    FolderRow,
    "id" | "name" | "color" | "parent_id" | "created_at" | "updated_at" | "encrypted_data" | "iv"
  > {
  tempId?: number;
}

export type AddFolder = Pick<FolderRow, "name" | "color" | "parent_id" | "user_id">;
export interface AddFolderRes
  extends Pick<
    FolderRow,
    "id" | "name" | "color" | "parent_id" | "created_at" | "updated_at" | "encrypted_data" | "iv"
  > {
  tempId?: number;
}

export interface UpdateFolderRes
  extends Pick<
    FolderRow,
    "id" | "name" | "color" | "parent_id" | "created_at" | "updated_at" | "encrypted_data" | "iv"
  > {
  tempId?: number;
}

async function fetchFolders(): Promise<FetchedFolder[]> {
  const { data, error } = await supabase
    .from("folders")
    .select(
      `
      id, 
      name, 
      color,
      parent_id,
      created_at,
      updated_at,
      encrypted_data,
      iv
    `
    )
    .order("name", { ascending: true });

  if (error) throw error;

  const dek = await loadKeyFromDB();

  return Promise.all(
    data.map(async (folder) => {
      const name = await processFolderName(folder, dek);
      return {
        ...folder,
        color: folder.color as FolderColor | null,
        name,
      };
    })
  );
}

async function fetchFolderById(id: number): Promise<FetchedFolder | null> {
  const { data, error } = await supabase
    .from("folders")
    .select(
      `
      id,
      name,
      color,
      parent_id,
      created_at,
      updated_at,
      encrypted_data,
      iv
    `
    )
    .eq("id", id)
    .single();

  if (error) throw error;

  const dek = await loadKeyFromDB();
  const name = await processFolderName(data, dek);

  return {
    ...data,
    color: data.color as FolderColor | null,
    name,
  };
}

async function addFolder(folder: AddFolder): Promise<AddFolderRes> {
  const dek = await loadKeyFromDB();
  const { encrypted_data, iv, clearName } = await prepareEncryptedFolderName(folder.name, dek);

  const insertData = {
    ...folder,
    ...(clearName && encrypted_data && iv ? { encrypted_data, iv, name: "" } : { encrypted_data: "", iv: "" }),
  };

  const { data, error } = await supabase
    .from("folders")
    .insert([insertData])
    .select("id, name, color, parent_id, created_at, updated_at, encrypted_data, iv")
    .single();

  if (error) throw error;

  return { ...data, color: folder.color, name: folder.name };
}

async function updateFolder({ id, folder }: { id: number; folder: FolderUpdate }): Promise<UpdateFolderRes | null> {
  const dek = await loadKeyFromDB();

  // Prepare encryption if name is being updated
  if (folder.name) {
    const { encrypted_data, iv, clearName } = await prepareEncryptedFolderName(folder.name, dek);

    if (clearName && encrypted_data && iv) {
      folder.encrypted_data = encrypted_data;
      folder.iv = iv;
      folder.name = "";
    }
  }

  const { data, error } = await supabase
    .from("folders")
    .update(folder)
    .eq("id", id)
    .select("id, name, color, parent_id, created_at, updated_at, encrypted_data, iv")
    .maybeSingle();

  if (error) throw error;

  return data ? { ...data, color: data.color as FolderColor | null } : null;
}

async function deleteFolder(id: number) {
  const { data, error } = await supabase.from("folders").delete().eq("id", id);
  if (error) throw error;
  return data;
}

export const foldersFetchers = {
  fetchFolders,
  fetchFolderById,
  addFolder,
  updateFolder,
  deleteFolder,
};
